import { Blackjack } from 'game-logic';
import { GameServices } from '../../services/game-services';
import { ChipStackViewModel } from '../chips/stack/chip-stack-view-model';
import { PlayerHandViewModel } from './player-hand-view-model';
import { RoundedNumberViewModel } from '../components/rounded-number-view-model';
import { BehaviorSubject, Subject } from 'rxjs';
import { delay } from 'src/games/common/helper';
import { UxMetrics } from '../../model/ux-metrics';
import { BlackjackStylesType, BlackjackTranslationsType } from '../../model/blackjack-types';

export class PlayerBettingBoxViewModel {
  public get layoutConfig() {
    return this.gameServices.gameMetricsService.gameLayoutConfig;
  }
  public get onGameSetupStart$() {
    return this.gameServices.blackjackGameRunner.onGameSetupStart$;
  }
  public get logicEventQueue$() {
    return this.gameServices.blackjackGameRunner.logicEventQueue$;
  }
  public get borderColor() {
    return this.gameServices.gameThemeService.getNumericalRGB(BlackjackStylesType.BorderColor);
  }
  public get borderAlpha() {
    return this.gameServices.gameThemeService.getNumericalAlpha(BlackjackStylesType.BorderColor);
  }
  public get onResetGameScene$() {
    return this.onResetGameScene;
  }
  public get interfaceEventQueue() {
    return this.gameServices.interfaceEventQueue;
  }
  public get playerHandChanges$() {
    return this.playerHandChanges.asObservable();
  }
  public get playerIdNow() {
    return this.currentPlayerId.getValue();
  }
  public get currentPlayerId$() {
    return this.currentPlayerId.asObservable();
  }

  public readonly chipStackViewModel: ChipStackViewModel;
  public readonly betAmountViewModel: RoundedNumberViewModel;
  public playerHandViewModels: Map<Blackjack.PileId, PlayerHandViewModel> = new Map();
  private readonly playerHandChanges = new Subject<{
    type: 'add' | 'remove';
    viewModel: PlayerHandViewModel;
  }>();
  private readonly currentPlayerId = new BehaviorSubject<Blackjack.PileId>(
    Blackjack.PileId.Player1,
  );
  private readonly onResetGameScene = new Subject<void>();

  private jitterSeed: number;

  constructor(private gameServices: GameServices) {
    this.jitterSeed = generateSeed();
    this.chipStackViewModel = new ChipStackViewModel(gameServices, this.jitterSeed);
    this.betAmountViewModel = new RoundedNumberViewModel(
      gameServices,
      BlackjackTranslationsType.Bet,
      0,
      gameServices.gameThemeService.getNumericalRGB(BlackjackStylesType.CardBackground),
      gameServices.gameThemeService.getNumericalAlpha(BlackjackStylesType.CardBackground),
    );

    const playerViewModel = new PlayerHandViewModel(
      this.gameServices,
      Blackjack.PileId.Player1,
      this.jitterSeed,
    );
    this.playerHandViewModels.set(playerViewModel.playerId, playerViewModel);
  }

  public resetGameScene() {
    for (const [pileId, viewModel] of this.playerHandViewModels) {
      if (pileId === Blackjack.PileId.Player1) {
        viewModel.resetGameScene();
      } else {
        this.playerHandChanges.next({
          type: 'remove',
          viewModel: viewModel,
        });
        this.playerHandViewModels.delete(pileId);
      }
    }

    this.clearPlayerChips();

    this.onResetGameScene.next();
  }

  public setBetStackAmount(betAmount: number): void {
    this.betAmountViewModel.setNumber(betAmount);
  }

  public chipStackCleared(): void {
    this.chipStackViewModel.chipStackCleared();
  }

  public chipStackIncreased(chip: Blackjack.ChipDenomination) {
    this.chipStackViewModel.chipStackIncreased(chip, true);
  }

  public clearPlayerChips(): void {
    this.gameServices.interfaceController.clearCurrentBet();
    this.jitterSeed = generateSeed();
    this.chipStackViewModel.setJitterSeed(this.jitterSeed);

    this.playerHandViewModels.forEach((viewModel, _) => {
      viewModel.setJitterSeed(this.jitterSeed);
    });
  }

  public clearPlayer(player: Blackjack.Player): void {
    if (player.playerId === Blackjack.PileId.Player1) {
      // Always leave Player1
      return;
    }
    const playerViewModel = this.playerHandViewModels.get(player.playerId);
    if (playerViewModel) {
      this.playerHandChanges.next({
        type: 'remove',
        viewModel: playerViewModel,
      });
    }
    this.playerHandViewModels.delete(player.playerId);
  }

  public async splitPlayer(
    player: Blackjack.Player,
    nextPlayer: Blackjack.Player,
    cardIndex: number | undefined,
  ): Promise<void> {
    // Create next player
    const nextPlayerViewModel = this.addNewPlayer(nextPlayer);

    // Animate to the next player, with a delay
    await this.animateToPlayer(nextPlayer);

    // Animate chip stack into mini-bet-view for next player
    await this.playerBetSplit(nextPlayer);

    // Move card from player to nextPlayer
    const playerViewModel = this.playerHandViewModels.get(player.playerId);
    await playerViewModel?.transferCardToCurrent(cardIndex);
    playerViewModel?.updateScore(player);

    // Add card into nextPlayer
    nextPlayerViewModel.addCard(nextPlayer, cardIndex);
    nextPlayerViewModel.updateScore(nextPlayer);

    // Remove card from player
    playerViewModel?.removeCard(cardIndex);

    await this.animateToPlayer(player);
  }

  public async moveNextPlayer(player: Blackjack.Player): Promise<void> {
    await this.animateToPlayer(player);
  }

  private addNewPlayer(player: Blackjack.Player): PlayerHandViewModel {
    const nextPlayerViewModel = new PlayerHandViewModel(
      this.gameServices,
      player.playerId,
      this.jitterSeed,
    );
    this.playerHandViewModels.set(nextPlayerViewModel.playerId, nextPlayerViewModel);
    this.playerHandChanges.next({
      type: 'add',
      viewModel: nextPlayerViewModel,
    });
    return nextPlayerViewModel;
  }

  private async animateToPlayer(player: Blackjack.Player): Promise<void> {
    this.currentPlayerId.next(player.playerId);
    await delay(UxMetrics.PlayerSwitchDelay);
  }

  private async playerBetSplit(player: Blackjack.Player): Promise<void> {
    const found = this.playerHandViewModels.get(player.playerId);
    if (found) {
      await found.playerBetSplit(player);
    }
  }
}

function generateSeed(): number {
  return Math.random() * 1000000000;
}
